import { useQuery } from "@tanstack/react-query";
import type { EntitlementKey } from "@shared/entitlements";
import type { AccountKind, PlanTier } from "@shared/modular-pricing";

interface AccountPlanEntitlements {
  accountKind?: AccountKind | null;
  planTier?: PlanTier | null;
  entitlements?: string[];
  billingActive?: boolean;
  trial?: { active: boolean; daysLeft: number };
}

/**
 * Client-side view of the modular entitlement set (the server's
 * `enforceEntitlements` remains the hard gate — this only drives UX: lock
 * badges, upgrade prompts, hiding dead-end actions).
 *
 * Legacy pre-cutover accounts (409 from the endpoint) read as `isModular:
 * false` with every `has()` true, so legacy tier gating keeps sole control.
 * While loading, `has()` is also true — gated pages render optimistically and
 * the server's 403 drives the in-context upgrade panel (matching the
 * existing tier-lock overlay behavior: never flicker a lock at an entitled
 * user).
 */
export function useEntitlements() {
  const query = useQuery<AccountPlanEntitlements>({
    queryKey: ["/api/app/account/plan"],
    retry: false,
    staleTime: 60_000,
  });

  const isModular = !!query.data?.accountKind;
  const set = new Set(query.data?.entitlements ?? []);

  return {
    isLoading: query.isLoading,
    isModular,
    accountKind: query.data?.accountKind ?? null,
    planTier: query.data?.planTier ?? null,
    billingActive: query.data?.billingActive ?? true,
    trialActive: query.data?.trial?.active ?? false,
    /** True when the feature is available (or gating doesn't apply yet). */
    has: (key: EntitlementKey): boolean => {
      if (query.isLoading || !isModular) return true;
      return set.has(key);
    },
  };
}
